// ─── V6: lifecycle + desync kill + fresh-lifecycle rebirth ───────────────────
export const BG_FRAGMENT = `
  precision mediump float;

  uniform vec3  uBgColor;
  uniform vec3  uRectColor;
  uniform float uSeed;
  uniform float uCount;
  uniform float uWidthMin;
  uniform float uWidthMax;
  uniform float uHeightMin;
  uniform float uHeightMax;
  uniform float uBlur;
  uniform float uIntensityMin;
  uniform float uIntensityMax;
  uniform float uGlobalIntensity;
  uniform float uTime;
  uniform float uTimeScale;      // rect animation speed (centerY oscillation)
  uniform float uLifeScale;      // lifecycle speed (birth→death cycle rate)
  uniform float uDispMin;
  uniform float uDispMax;
  uniform float uCenterWidth;
  uniform float uUVScale;
  uniform float uLifeMin;
  uniform float uLifeMax;
  uniform float uKill;           // 0=all alive, 1=all dead — desync via per-rect threshold
  uniform float uReseedTime;     // uTime when last seed change happened (0 = never)
  uniform float uReseedDuration; // seconds over which rects are staggered on rebirth

  varying vec2 vUv;

  float hash(float n) {
    return fract(sin(n) * 43758.5453123);
  }

  float softRect(vec2 uv, vec2 center, vec2 size, float blur) {
    vec2 d = abs(uv - center) - size;
    float outside = length(max(d, 0.0));
    float inside  = min(max(d.x, d.y), 0.0);
    return 1.0 - smoothstep(0.0, blur, outside + inside);
  }

  void main() {
    float acc = 0.0;

    for (int i = 0; i < 32; i++) {
      if (float(i) >= uCount) break;

      float s = uSeed + float(i) * 17.0;

      // ─── KILL (desynchronized per rect) ──────────────────────────────
      // killThreshold is remapped to [0.15, 0.85] so the smoothstep band
      // [kT-0.15, kT+0.15] always stays within [0, 1].
      // This guarantees killFade=0 when uKill=1 and killFade=1 when uKill=0
      // for every rect, with no boundary bleed.
      float killThreshold = mix(0.15, 0.85, hash(s * 11.23 + 7.0));
      float killFade = 1.0 - smoothstep(
        killThreshold - 0.15,
        killThreshold + 0.15,
        uKill
      );

      // ─── LIFECYCLE ────────────────────────────────────────────────────
      // After a reseed (uReseedTime > 0), each rect is born fresh from t=0
      // at a staggered time: high killThreshold rects are born first,
      // low killThreshold rects are born last — mirroring the death order.
      float life    = mix(uLifeMin, uLifeMax, hash(s * 9.13));
      float tOffset = hash(s * 10.31) * 100.0;

      float stagger  = (1.0 - killThreshold) * uReseedDuration;
      float freshAge = max(0.0, uTime - uReseedTime - stagger) * uLifeScale;
      float contAge  = uTime * uLifeScale + tOffset;
      float useFresh = step(0.001, uReseedTime);
      float tGlobal  = mix(contAge, freshAge, useFresh);

      float t = mod(tGlobal, life) / life;

      float lifeFade =
        smoothstep(0.0, 0.15, t) *
        (1.0 - smoothstep(0.85, 1.0, t));

      float cycle  = floor(tGlobal / life);
      float sCycle = s + cycle * 13.37;

      // ─── RECT PROPERTIES ─────────────────────────────────────────────
      float cx = 0.5 + (hash(sCycle * 1.73 + 0.1) - 0.5) * uUVScale;
      float cy = 0.5 + (hash(sCycle * 2.37 + 0.4) - 0.5) * uUVScale;

      float w    = (uWidthMin  + hash(sCycle * 3.11 + 1.0) * (uWidthMax  - uWidthMin)) * uUVScale;
      float h    = (uHeightMin + hash(sCycle * 4.73 + 2.0) * (uHeightMax - uHeightMin)) * uUVScale;
      float blur = uBlur * uUVScale;

      float mask = softRect(vUv, vec2(cx, cy), vec2(w, h), blur);

      float iCenter = uIntensityMin + hash(sCycle * 5.29 + 3.0) * (uIntensityMax - uIntensityMin);
      float iEdge   = uIntensityMin + hash(sCycle * 6.17 + 4.0) * (uIntensityMax - uIntensityMin);

      float dispAmp = (uDispMin + hash(sCycle * 7.41 + 5.0) * (uDispMax - uDispMin)) * uUVScale;
      float phase   = hash(sCycle * 8.13 + 6.0) * 6.28318;

      // animation uses uTimeScale — independent of lifecycle speed
      float centerY = cy + sin(uTime * uTimeScale + phase) * dispAmp;

      float dist = clamp(abs(vUv.y - centerY) / max(h, 0.001), 0.0, 1.0);

      float remapped = clamp(
        (dist - uCenterWidth) / max(1.0 - uCenterWidth, 0.001),
        0.0,
        1.0
      );

      // ─── ACCUMULATION ─────────────────────────────────────────────────
      acc += mask * mix(iCenter, iEdge, remapped) * lifeFade * killFade;
    }

    float intensity = clamp(acc, 0.0, 1.0) * uGlobalIntensity;

    gl_FragColor = vec4(mix(uBgColor, uRectColor, intensity), 1.0);
  }
`;
